import React, { FC, useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { globalStore } from '../utils/globalStore';
import { getMenuPermissionsApi } from '../service/menu';
import { getUserInfoApi } from '../service/user';
import { Menu } from '../service/menu/types';
import { convertNewMenuToOldFormat } from '../utils/initService';
import {
  normalizeMenuPath,
  MARKETING_PAGE_PATH,
  APP_LAYOUT_BASE,
  PREMIUM_SERIES_INNER_BASE,
  PREMIUM_SERIES_MANAGE_FULL_PATH,
  PREMIUM_SYSTEM_MANAGE_BASE,
  PREMIUM_SYSTEM_MANAGE_FULL_PATHS,
  PREMIUM_TRANSLATION_FULL_PATH,
  PREMIUM_VIDEO_LIST_REFACTOR_BASE,
  PREMIUM_UPLOAD_VIDEO_WORKSPACE_PATH,
  PREMIUM_VOICE_DESIGN_FULL_PATH,
  PREMIUM_VOICE_LIBRARY_FULL_PATH,
  PREMIUM_VOICE_LIBRARY_PUBLIC_PATH,
  PREMIUM_VOICE_LIBRARY_SERIES_PATH,
  QUICK_SERIES_INNER_BASE,
  QUICK_TRANSLATE_FULL_PATH,
  getFirstMigratedSystemManagePathFromMenus,
  menuPathMigratedToSystemWorkspace,
  resolveMenuNavigationPath,
} from '../utils/menuPathNormalize';
import { NoAccess } from '../pages/error/NoAccess';
import { resolvePermissionByPath, HUB_ROUTE_COMPONENT, isSuperAdmin } from './routePermissionResolver';
import Auth from "./auth";

// 静态导入常用组件（不需要动态导入）
import { Spin } from "antd";

/** 仅菜动态路由就绪前使用：始终浅色底，与系统深色偏好无关，避免与全局黑色 Spin 冲突 */
const PAGE_LOADING_WRAP_CLASS =
  "route-boot-page-loading flex min-h-[200px] w-full max-w-md mx-auto items-center justify-center rounded-2xl bg-white p-10 max-md:min-h-[150px] max-md:p-8";
const PAGE_LOADING_INNER_CLASS = "flex flex-col items-center justify-center gap-3";
const PAGE_LOADING_TEXT_CLASS = "text-center text-sm font-medium leading-snug text-gray-500";

const PageLoading: React.FC<{ text?: string }> = ({ text }) => (
  <div className={PAGE_LOADING_WRAP_CLASS}>
    <div className={PAGE_LOADING_INNER_CLASS}>
      <Spin size="large" className="route-boot-page-loading__spin" />
      {text ? <div className={PAGE_LOADING_TEXT_CLASS}>{text}</div> : null}
    </div>
  </div>
);

import { AuthPage } from "../pages/auth/AuthPage";
import { RegisterRedirect } from "../pages/auth/RegisterRedirect";
import { Home } from "../pages/home/Home";
import { HomeRootLayout } from "../pages/home/HomeRootLayout";
import { PremiumWorkspaceLayout } from "../pages/home/PremiumWorkspaceLayout";
import { PremiumSeriesInnerPage } from "../pages/premiumSeries/PremiumSeriesInnerPage";
const PremiumVideoListRefactorPage = React.lazy(() =>
  import("../pages/videoListRefactor/PremiumVideoListRefactorPage").then((m) => ({
    default: m.PremiumVideoListRefactorPage,
  }))
);
import { PremiumUploadVideoWorkspacePage } from "../pages/uploadWorkspace/PremiumUploadVideoWorkspacePage";
import { RootRedirect } from "./RootRedirect";
import { Profile } from "../pages/profile/Profile";
import VideoEdit from "../pages/videoEdit";
import { VoiceLibraryEntryRedirect } from "../pages/voiceLibrary/VoiceLibraryEntryRedirect";
import { VoiceLibraryWorkspaceOutlet } from "../pages/voiceLibrary/VoiceLibraryWorkspaceOutlet";
import { VoiceDesignWorkspacePage } from "../pages/voiceLibrary/VoiceDesignWorkspacePage";
import { VoiceLibraryWorkspacePage } from "../pages/voiceLibrary/VoiceLibraryWorkspacePage";
import { VoicePublic } from "../pages/voiceLibrary/public/Index";
import { VoiceDesign } from "../pages/voiceDesign/Index";
import { VoiceClone } from "../pages/voiceClone/Index";
import { SubtitleTrans } from "../pages/subtitleTrans/Index";
import { TextToAudio } from "../pages/textToAudio/Index";
import { VoiceAi } from "../pages/voiceAi/Index";
import { AiImage } from "../pages/aiImage/Index";
import { AiImageDetail } from "../pages/aiImage/Detail";
import { AiPodcast } from "../pages/podcast/Index";
import { InvoiceManagement } from "../pages/invoiceManagement/InvoiceManagement";
import { AiVideo } from "../pages/aiVideo/Index";
import { AiVideoDetail } from "../pages/aiVideo/Detail";
import { QuickTrans } from "../pages/quickTrans/QuickTrans";
import { QuickTranslateHome } from "../pages/quickTrans/QuickTranslateHome";
import { QuickSeriesListIn } from "../pages/quickSeriesListIn/QuickSeriesListIn";
import { QuickSeriesListInLegacyRedirect } from "../pages/quickSeriesListIn/QuickSeriesListInLegacyRedirect";
import { QuickVideoListLegacyRedirect } from "../pages/quickVideoList/QuickVideoListLegacyRedirect";
import { Marketing } from "../pages/marketing/Marketing";
import { ApiDocPage } from "../pages/marketing/ApiDocPage";
import { AgentPage } from "../pages/agent/AgentPage";
import { PricingPage } from "../pages/pricing/PricingPage";
import { SystemManageWorkspaceLayout } from "../pages/manage/SystemManageWorkspaceLayout";
import { LycheeHub } from "../pages/marketing/LycheeHub";
import { AiDigitalHuman } from "../pages/aiDigitalHuman/Index";
import { AiDigitalHumanStudio } from "../pages/aiDigitalHuman/Studio";
import { AiDigitalHumanAvatars } from "../pages/aiDigitalHuman/Avatars";
import { AiDigitalHumanTemplates } from "../pages/aiDigitalHuman/Templates";
import { AiTranscription } from "../pages/transcription/Index";

// 动态组件映射表 - 使用 React.lazy 解决生产环境路径问题
const dynamicComponentMap: Record<string, React.LazyExoticComponent<React.ComponentType<any>>> = {
  'pages/profile/Profile': React.lazy(() => import('../pages/profile/Profile').then(m => ({ default: m.Profile }))),
  'pages/manage/BasicInfo': React.lazy(() => import('../pages/manage/BasicInfo').then(m => ({ default: m.BasicInfo }))),
  'pages/manage/GroupManage': React.lazy(() => import('../pages/manage/GroupManage').then(m => ({ default: m.GroupManage }))),
  'pages/manage/JujiManage': React.lazy(() => import('../pages/manage/JujiManage').then(m => ({ default: m.JujiManage }))),
  'pages/manage/system/MenuManage': React.lazy(() => import('../pages/manage/system/MenuManage').then(m => ({ default: m.MenuManage }))),
  'pages/manage/system/RoleManage': React.lazy(() => import('../pages/manage/system/RoleManage').then(m => ({ default: m.RoleManage }))),
  'pages/manage/system/UserInfo': React.lazy(() => import('../pages/manage/system/UserInfo').then(m => ({ default: m.UserInfo }))),
  'pages/manage/system/log/Loginlog': React.lazy(() => import('../pages/manage/system/log/Loginlog').then(m => ({ default: m.Loginlog }))),
  'pages/manage/system/log/Operationlog': React.lazy(() => import('../pages/manage/system/log/Operationlog').then(m => ({ default: m.Operationlog }))),
  'pages/manage/system/voiceManage': React.lazy(() => import('../pages/manage/system/voiceManage').then(m => ({ default: m.VoiceManage }))),
  'pages/subtitleOverlayPlayer': React.lazy(() => import('../pages/subtitleOverlayPlayer')),
};

// 路由配置接口
interface RouteConfig {
  path: string;
  component: string;
  requireAuth: boolean;
  permission?: string;
  parentId?: number;
  orderNum?: number;
  icon?: string;
  menuName?: string;
}

// 静态路由配置（不需要权限控制）
const staticRouteConfigs: RouteConfig[] = [
  { path: '/login', component: 'pages/auth/AuthPage', requireAuth: false },
  { path: '/register', component: 'pages/auth/RegisterRedirect', requireAuth: false },
  /**
   * 勿再单独注册 `path: "/home/"` Navigate：React Router 7 会把 `/home` 匹配到该路由，造成重定向异常与白屏
   * ──────────────────────────────────────────────────────────────────────────────────────────
   * ⚠️ `/home` = 官网首页宣传落地页（Marketing，无需登录，原为 `/marketing`，见 MARKETING_PAGE_PATH）。
   *    请勿把它当成平台工作台首页：精品译制等平台功能位于 `/hub/premium-translation`，
   *    其代码组件放在 `src/pages/home/`（HomeShell / PremiumWorkspaceLayout 等）——那是「组件目录」叫 home，
   *    与「路由 `/home` 营销页」是两个概念，改路由时务必区分。
   */
  { path: '/home', component: 'pages/marketing/Marketing', requireAuth: false },
  { path: '/api_doc', component: 'pages/marketing/ApiDocPage', requireAuth: false },
  { path: '/agent', component: 'pages/agent/AgentPage', requireAuth: false },
  { path: '/pricing', component: 'pages/pricing/PricingPage', requireAuth: false },
  { path: '/lycheeHub', component: 'pages/marketing/LycheeHub', requireAuth: false },
];

// 默认路由配置（需要权限控制（判断是否有token））
// 注：精品译制 path /hub/premium-translation，由嵌套路由单独注册，见 Routes HomeRootLayout
const defaultRouteConfigs: RouteConfig[] = [
  {
    path: "/quickSeriesListIn",
    component: "pages/quickSeriesListIn/QuickSeriesListInLegacyRedirect",
    requireAuth: true,
  },
  {
    path: "/quickVideoList",
    component: "pages/quickVideoList/QuickVideoListLegacyRedirect",
    requireAuth: true,
  },
  { path: '/videoEdit/:id', component: 'pages/videoEdit', requireAuth: true },
  { path: '/subtitle-overlay-player/:id?', component: 'pages/subtitleOverlayPlayer', requireAuth: true },
  { path: '/voice-library', component: 'pages/voiceLibrary/Index', requireAuth: true },
  { path: '/voice-design', component: 'pages/voiceDesign/Index', requireAuth: true },
  // 独立的管理页面路
//   { path: '/profile', component: 'pages/profile/Profile', requireAuth: true },
//   { path: '/manage/menu', component: 'pages/manage/MenuManagement', requireAuth: true, permission: 'admin:all' },
//   { path: '/manage/juji', component: 'pages/manage/JujiManage', requireAuth: true },
//   { path: '/manage/group', component: 'pages/manage/GroupManage', requireAuth: true },
//   { path: '/manage/basic', component: 'pages/manage/BasicInfo', requireAuth: true },
  
];

// 简化的路由工具函数
const mergeRoutes = (staticRoutes: RouteConfig[], dynamicRoutes: RouteConfig[], defaultRoutes: RouteConfig[]): RouteConfig[] => {
  const pathMap = new Map<string, RouteConfig>();
  
  // 添加静态路由（优先级最高）
  staticRoutes.forEach(route => pathMap.set(route.path, route));
  
  // 添加动态路由（优先级中等）
  dynamicRoutes.forEach(route => {
    if (!pathMap.has(route.path)) {
      pathMap.set(route.path, route);
    }
  });
  
  // 添加默认路由（优先级最低）
  defaultRoutes.forEach(route => {
    if (!pathMap.has(route.path)) {
      pathMap.set(route.path, route);
    }
  });
  
  return Array.from(pathMap.values());
};

/** 工作台嵌套路由：合并菜单路由时去掉平铺项，避免与 /hub 下子路由重复 */
const PREMIUM_ROUTE_PATHS = new Set<string>([
  PREMIUM_TRANSLATION_FULL_PATH,
  PREMIUM_SERIES_INNER_BASE,
  PREMIUM_SERIES_MANAGE_FULL_PATH,
  PREMIUM_VIDEO_LIST_REFACTOR_BASE,
  PREMIUM_UPLOAD_VIDEO_WORKSPACE_PATH,
  PREMIUM_VOICE_LIBRARY_FULL_PATH,
  PREMIUM_VOICE_LIBRARY_PUBLIC_PATH,
  PREMIUM_VOICE_LIBRARY_SERIES_PATH,
  PREMIUM_VOICE_DESIGN_FULL_PATH,
  `${APP_LAYOUT_BASE}/ai-voice`,
  `${APP_LAYOUT_BASE}/ai-image`,
  `${APP_LAYOUT_BASE}/podcast`,
  `${APP_LAYOUT_BASE}/ai-video`,
  `${APP_LAYOUT_BASE}/invoice-management`,
  QUICK_SERIES_INNER_BASE,
  "/premium-translation",
  "/quick-translate",
  QUICK_TRANSLATE_FULL_PATH,
  ...PREMIUM_SYSTEM_MANAGE_FULL_PATHS,
  `${APP_LAYOUT_BASE}/ai-digital-human`,
  `${APP_LAYOUT_BASE}/ai-digital-human/avatars`,
  `${APP_LAYOUT_BASE}/ai-digital-human/templates`,
  `${APP_LAYOUT_BASE}/transcription`,
]);

const mergeAppRoutes = (dynamicRoutes: RouteConfig[]): RouteConfig[] => {
  const filteredDynamic = dynamicRoutes.filter((r) => !PREMIUM_ROUTE_PATHS.has(r.path));
  return mergeRoutes(staticRouteConfigs, filteredDynamic, defaultRouteConfigs);
};

// 递归处理菜单数据，提取所有路
const extractRoutesFromMenu = (menu: Menu.Menu): RouteConfig[] => {
  const routes: RouteConfig[] = [];
  
  
  // 处理当前菜单
  if (!menu.hidden && menu.path && menu.component) {
    // 只有明确设置了权限的路由才需要权限检
    const menuPerms = (menu as any).perms;
    const hasExplicitPermission = menuPerms && menuPerms.trim() !== '';
    
    // 处理component值：去掉前面/"
    let component = menu.component.replace(/^\/+/, '');
    const rawMenuPath = (menu.path || "").trim();
    const normalizedPath = resolveMenuNavigationPath(menu.path, menu.component);
    if (rawMenuPath === "/voice-design" && component === "pages/voicePublic/Index") {
      component = "pages/voiceDesign/Index";
    }

    const route = {
      path: normalizedPath,
      component: component,
      requireAuth: true,
      permission: hasExplicitPermission ? menuPerms : undefined, // 只有明确有权限时才设
      icon: menu.meta?.icon,
      menuName: menu.meta?.title
    };
    /** 工作/hub 下页面已Routes 中静态嵌套注册；若此处再 push，会与嵌套路由竞态，误渲染菜单里的旧 component（如 VoicePublic*/
    if (!PREMIUM_ROUTE_PATHS.has(normalizedPath)) {
      routes.push(route);
    }
  } else {
  }
  
  // 递归处理子菜
  if (menu.children && menu.children.length > 0) {
    menu.children.forEach(child => {
      routes.push(...extractRoutesFromMenu(child));
    });
  }
  
  return routes;
};

const menuToRoutes = (menuList: Menu.Menu[]): RouteConfig[] => {
  const allRoutes: RouteConfig[] = [];
  
  menuList.forEach(menu => {
    const routes = extractRoutesFromMenu(menu);
    allRoutes.push(...routes);
  });
  
  // 所有动态路由已生成
  return allRoutes;
};
// 约定式路由：菜单中的component字段直接对应文件路径
// 例如：component: 'pages/manage/BasicInfo' 对应文件 src/pages/manage/BasicInfo.tsx

// 递归检查菜单权
const checkMenuPermission = (menu: any, permission: string): boolean => {
  // 检查当前菜单的权限 - 只检meta.perms 字段
  if (menu.meta?.perms === permission) {
    return true;
  }
  
  // 递归检查子菜单
  if (menu.children && menu.children.length > 0) {
    return menu.children.some((child: any) => checkMenuPermission(child, permission));
  }
  
  return false;
};

// 权限检查函- 适配新的菜单数据结构
// 三层鉴权（与后端 get-permission-info 一一对应）：
//   第1层 userInfo.permissions[] 精确匹配
//   第2层 后端下发的 menu tree 中 meta.perms 命中（即使 permissions[] 没里，菜单被授权也算）
//   第3层 角色级兑底：roles.includes('super_admin')
const hasPermission = (permission: string): boolean => {
  // 1. 先查内存 (Store)
  let permissions = globalStore.getPermissions();

  // === 🚑 修复补丁开始：内存丢失时的自动急救 ===
  if (!permissions || permissions.length === 0) {
    try {
      const cachedUserStr = localStorage.getItem('globalUserInfo');
      if (cachedUserStr) {
        const cachedUser = JSON.parse(cachedUserStr);
        if (cachedUser.permissions && cachedUser.permissions.length > 0) {
          console.warn(`⚠️ [权限恢复] 检测到内存权限丢失，已使用本地缓存兜底: ${permission}`);
          permissions = cachedUser.permissions;
        }
      }
    } catch (e) {
      console.error('尝试恢复权限失败', e);
    }
  }
  // === 🚑 修复补丁结束 ===

  // 第一层：精确匹配 userInfo.permissions[]
  if (permissions && permissions.length > 0 && permissions.includes(permission)) {
    return true;
  }

  // 第二层：后端 menu tree 中 meta.perms 命中
  const menuList = globalStore.getMenuList();
  if (menuList.some(menu => checkMenuPermission(menu, permission))) {
    return true;
  }

  // 第三层：角色级兑底 — 后端 roles: ["super_admin"]
  //   InitService 会把字符串数组转成 { role_key, code } 对象数组，
  //   所以必须兼容两种格式，并只取稳定标识字段（role_key / code），不用 name（展示名会随文案变）。
  const roles = globalStore.getUserInfo()?.roles ?? [];
  const roleKeys = roles
    .map((r: any) => {
      if (typeof r === 'string') return r;
      return r?.role_key || r?.code || '';
    })
    .filter(Boolean)
    .map((s: string) => s.toLowerCase());
  if (roleKeys.includes('super_admin')) {
    return true;
  }

  return false;
};

// 权限守卫组件 - 数据驱动：从后端 menu tree 反查权限字符串
// 用法：
//   - Hub 壳层路由：<PermissionGuard routePath="/hub/seriesManage">...</PermissionGuard>
//   - 菜单驱动的旧路径路由：<PermissionGuard permission={menu.meta.perms}>...</PermissionGuard>
const PermissionGuard: React.FC<{
  routePath?: string;
  permission?: string;
  children: React.ReactNode;
}> = ({ routePath, permission, children }) => {
  // 0. 超管直接放行 — 不依赖 menu / permission 是否配齐
  if (globalStore.isInitialized() && isSuperAdmin()) {
    return <>{children}</>;
  }

  // 1. 优先级：显式 permission > 从 routePath 反查
  let requiredPermission = permission;
  if (!requiredPermission && routePath) {
    requiredPermission = resolvePermissionByPath(routePath);
  }

  // 2. 没法解析出权限字符串：
  //    - 已知受护路由（在 HUB_ROUTE_COMPONENT 桥接表里）→ fail-closed
  //      意味着后端菜单存在但当前用户未被授权 → 拒绝
  //    - 未知路由（不在桥接表）→ fail-open + dev warn
  //      可能是前端独有 / 后端暂未配置 menu，不应误锁
  if (!requiredPermission) {
    const isKnownProtectedRoute = !!routePath && !!HUB_ROUTE_COMPONENT[routePath];
    if (isKnownProtectedRoute) {
      return <NoAccess />;
    }
    if (import.meta.env.DEV) {
      console.warn(`[PermissionGuard] 无可用鉴权字符串（routePath=${routePath}, permission=${permission}），不设门槛`);
    }
    return <>{children}</>;
  }

  // 3. 尚未初始化（首次加载 / 接口未回）→  乐观放行，避免白屏 / 全屏闪 403
  //    这里不能用「menuList.length === 0」判断，因为后端对无权限用户
  //    本就返回空 menus，这种情况下必须严格拒绝。靠 isInitialized 区分。
  if (!globalStore.isInitialized()) {
    return <>{children}</>;
  }

  // 4. 已初始化：严格鉴权（3层：permissions[] / menu tree / super_admin）
  if (!hasPermission(requiredPermission)) {
    return <NoAccess />;
  }

  return <>{children}</>;
};

// 错误组件 - 统一的错误提
const ErrorComponent: React.FC<{ componentName: string }> = ({ componentName }) => (
  <div style={{ 
    padding: '20px', 
    textAlign: 'center', 
    color: '#666',
    background: '#f5f5f5',
    borderRadius: '8px',
    margin: '20px'
  }}>
    <h3>页面加载失败</h3>
    <p>组件 {componentName} 加载失败，请刷新页面重试</p>
    <button 
      onClick={() => window.location.reload()} 
      style={{
        padding: '8px 16px',
        background: '#1890ff',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer'
      }}
    >
      刷新页面
    </button>
  </div>
);

const getDynamicComponent = (componentName: string) => {
  // 优先使用组件映射表中lazy 组件
  const LazyComponent = dynamicComponentMap[componentName];
  
  if (LazyComponent) {
    return (
      <React.Suspense fallback={<PageLoading />}>
        <LazyComponent />
      </React.Suspense>
    );
  }
  
  // 如果映射表中没有，直接返回错误组
  return <ErrorComponent componentName={componentName} />;
};

// 动态路由组
export const DynamicRoutes: FC = () => {
  const [routes, setRoutes] = useState<RouteConfig[]>(() => {
    // 立即使用默认路由，避免白
    return mergeAppRoutes([]);
  });
  const [menuList, setMenuList] = useState<any[]>(() => globalStore.getMenuList());

  useEffect(() => {
    const loadRoutes = async () => {
      try {
        // 检查是否有token，如果没有token就不加载菜单数据
        const token = localStorage.getItem("token");
        if (!token) {
          console.log('没有token，跳过菜单数据加');
          // 使用默认路由
          const fallbackRoutes = mergeAppRoutes([]);
          setRoutes(fallbackRoutes);
          return;
        }

        // 获取菜单权限和用户信息（新接口已包含用户信息，不再需要单独请求）
        const menuResponse = await getMenuPermissionsApi();
        
        // 处理新的响应格式
        let menuList: Menu.Menu[] = [];
        let userInfo: any = null;
        let roles: string[] = [];
        let permissions: string[] = [];
        
        if (menuResponse && menuResponse.code === 0 && menuResponse.data) {
          const responseData = menuResponse.data;
          
          // 转换菜单数据格式
          if (responseData.menus && Array.isArray(responseData.menus)) {
            menuList = responseData.menus.map(menu => convertNewMenuToOldFormat(menu));
          }
          
          // 保存用户信息
          if (responseData.user) {
            userInfo = {
              ...responseData.user,
              roles: responseData.roles || [],
              permissions: responseData.permissions || []
            };
          }
          
          roles = responseData.roles || [];
          permissions = responseData.permissions || [];
        } else {
          console.error('获取菜单权限失败:', menuResponse);
        }
        
        // 保存用户信息到全局状
        if (userInfo) {
          globalStore.setUserInfo(userInfo);
        }
        
        // 更新全局状态和本地状
        globalStore.setMenuList(menuList);
        globalStore.setInitialized(true);
        setMenuList(menuList);
        
        // 基于菜单权限生成动态路
        const allRoutes = generateDynamicRoutes(menuList);
        
        setRoutes(allRoutes);
      } catch (error) {
        console.error('加载菜单权限失败:', error);
        // 如果加载失败，使用默认路
        const fallbackRoutes = mergeAppRoutes([]);
        setRoutes(fallbackRoutes);
      }
    };

    loadRoutes();
  }, []);

  // 基于菜单权限生成动态路
  const generateDynamicRoutes = (menuList: any[]): RouteConfig[] => {
    const dynamicRoutes = menuToRoutes(menuList);
    return mergeAppRoutes(dynamicRoutes);
  };

  // 渲染路由组件
  const renderRoute = (route: RouteConfig, menuList: any[] = []) => {
    const { path, component, requireAuth, permission } = route;
    
    // 静态组件映射（不需要动态导入的组件
    const staticComponents: Record<string, React.ComponentType> = {
      'pages/auth/AuthPage': AuthPage,
      'pages/auth/RegisterRedirect': RegisterRedirect,
      'pages/home/Home': Home,
      'pages/premiumSeries/PremiumSeriesInnerPage': PremiumSeriesInnerPage,
      'pages/profile/Profile': Profile,
      'pages/videoEdit': VideoEdit,
      'pages/voiceLibrary/Index': VoiceLibraryEntryRedirect,
      'pages/voicePublic/Index': VoicePublic,
      'pages/voiceDesign/Index': VoiceDesign,
      'pages/voiceClone/Index': VoiceClone,
      'pages/subtitleTrans/Index': SubtitleTrans,
      'pages/textToAudio/Index': TextToAudio,
      'pages/voiceAi/Index': VoiceAi,
      'pages/aiImage/Index': AiImage,
      'pages/quickTrans/QuickTrans': QuickTrans,
      'pages/quickTrans/QuickTranslateHome': QuickTranslateHome,
      "pages/quickSeriesListIn/QuickSeriesListIn": QuickSeriesListIn,
      "pages/quickSeriesListIn/QuickSeriesListInLegacyRedirect": QuickSeriesListInLegacyRedirect,
      "pages/quickVideoList/QuickVideoListLegacyRedirect": QuickVideoListLegacyRedirect,
      "pages/quickVideoList/QuickVideoList": QuickVideoListLegacyRedirect,
      'pages/marketing/Marketing': Marketing,
      'pages/marketing/ApiDocPage': ApiDocPage,
      'pages/agent/AgentPage': AgentPage,
      'pages/pricing/PricingPage': PricingPage,
      'pages/marketing/LycheeHub': LycheeHub,
      'pages/aiDigitalHuman/Index': AiDigitalHuman,
    };
    
    // 检查是否为静态组
    const StaticComponent = staticComponents[component];
        
    // 创建带权限检查的组件包装- 使用解耦的权限守卫
    const ProtectedComponent = () => {
      return (
        <PermissionGuard permission={permission}>
          {/* 如果是静态组件，直接渲染 */}
          {StaticComponent ? (
            <StaticComponent />
          ) : (
            getDynamicComponent(component)
          )}
        </PermissionGuard>
      );
    };
    
    // 特殊处理Profile页面的嵌套路
    if (path === '/profile') {
      // 查找Profile菜单及其子菜单（个人中心的path可能是null，通过component或name匹配
      const profileMenu = menuList.find(menu => 
        menu.path === '/profile' || 
        menu.component === 'pages/profile/Profile' ||
        menu.name === '个人中心'
      );
      const profileMenus = profileMenu?.children || [];
      // Profile菜单数据已生
      
      // 递归函数：提取相对路径（相对/profile
      const extractRelativePath = (fullPath: string): string => {
        if (!fullPath || fullPath === '/profile') return '';
        
        // 严格提取 /profile/ 后的部分（适配所profile开头的子路由）
        const profilePrefix = '/profile/';
        if (fullPath.startsWith(profilePrefix)) {
          const relativePath = fullPath.slice(profilePrefix.length);
          return relativePath;
        }
        
        return fullPath;
      };
      
      // 递归函数：生成所有层级的嵌套路由
      // parentFullPath: 父菜单的完整路径（如 '/profile'），用于计算子路由的相对路径
      // isNested: 是否在嵌Route 中，如果是，子路由路径应该相对于父路
      const renderNestedRoutes = (menus: any[], parentFullPath: string = '/profile', isNested: boolean = false): React.ReactNode[] => {
        const routes: React.ReactNode[] = [];
        
        menus.forEach((menu: any) => {
          // 跳过隐藏的菜单，但不跳过空路空组件的父菜单（如系统管理）
          if (menu.hidden) {
            return;
          }
          
          const hasChildren = menu.children && menu.children.length > 0;
          const hasComponent = !!menu.component;
          const hasPath = !!menu.path && menu.path !== '/profile';
          
          // 情况1：有路径+有组生成子路由（如角色管理、用户管理、权限管理）
          if (hasPath && hasComponent) {
            // 已迁/hub 工作台的菜单项：不在个人中心下重复注册子路由
            if (menu.path.startsWith(`${APP_LAYOUT_BASE}/`)) {
              if (hasChildren) {
                const childRoutes = renderNestedRoutes(menu.children, parentFullPath, isNested);
                routes.push(...childRoutes);
              }
              return;
            }
            if (menuPathMigratedToSystemWorkspace(menu.path, menu.component)) {
              if (hasChildren) {
                const childRoutes = renderNestedRoutes(menu.children, parentFullPath, isNested);
                routes.push(...childRoutes);
              }
              return;
            }
            // 提取相对路径profile/role role
            const relativePath = extractRelativePath(menu.path);
            if (!relativePath) {
              return;
            }
            
            const component = menu.component.replace(/^\/+/, '');
            routes.push(
              <Route 
                key={menu.id || menu.name || menu.path} 
                path={relativePath} 
                element={getDynamicComponent(component)} 
              />
            );
            
            // 如果有子菜单，递归处理
            if (hasChildren) {
              const childRoutes = renderNestedRoutes(menu.children, parentFullPath, isNested);
              routes.push(...childRoutes);
            }
          }
          // 情况2：无路径/无组件，但有子菜递归处理子菜单（如系统管理、日志管理）
          else if (hasChildren) {
            // 递归处理子菜单，父路径沿用当前parentFullPath（因为当前菜单无路径
            const childRoutes = renderNestedRoutes(menu.children, parentFullPath, isNested);
            routes.push(...childRoutes);
          }
          // 情况3：有组件但无路径（如基础信息，path为null但有component
          else if (hasComponent && !hasPath) {
            // 这种情况可能需要特殊处理，暂时跳过
            console.log('跳过无路径但有组件的菜单:', menu.name);
          }
        });
        
        return routes;
      };
      
      // 查找第一个有路径的子菜单作为默认路由
      const findFirstValidPath = (menus: any[]): string | null => {
        for (const menu of menus) {
          if (menu.hidden) continue;
          
          // 如果菜单有路径和组件，直接返
          if (menu.path && menu.component && menu.path.startsWith('/profile/')) {
            if (!menuPathMigratedToSystemWorkspace(menu.path, menu.component)) {
              const relativePath = extractRelativePath(menu.path);
              if (relativePath) {
                return relativePath;
              }
            }
          }
          
          // 如果有子菜单，递归查找
          if (menu.children && menu.children.length > 0) {
            const childPath = findFirstValidPath(menu.children);
            if (childPath) {
              return childPath;
            }
          }
        }
        return null;
      };
      
      // 收集所有实际会生成的路由路
      const collectedPaths: string[] = [];
      
      // 修改 renderNestedRoutes，在生成路由的同时收集路
      const renderNestedRoutesWithPathCollection = (menus: any[], parentFullPath: string = '/profile', isNested: boolean = false): React.ReactNode[] => {
        const routes: React.ReactNode[] = [];
        
        menus.forEach((menu: any) => {
          if (menu.hidden) {
            return;
          }
          
          const hasChildren = menu.children && menu.children.length > 0;
          const hasComponent = !!menu.component;
          const hasPath = !!menu.path && menu.path !== '/profile';
          
          if (hasPath && hasComponent) {
            if (menu.path.startsWith(`${APP_LAYOUT_BASE}/`)) {
              if (hasChildren) {
                const childRoutes = renderNestedRoutesWithPathCollection(menu.children, parentFullPath, isNested);
                routes.push(...childRoutes);
              }
              return;
            }
            if (menuPathMigratedToSystemWorkspace(menu.path, menu.component)) {
              if (hasChildren) {
                const childRoutes = renderNestedRoutesWithPathCollection(menu.children, parentFullPath, isNested);
                routes.push(...childRoutes);
              }
              return;
            }
            const relativePath = extractRelativePath(menu.path);
            if (relativePath) {
              // 收集路径
              collectedPaths.push(relativePath);
              
              const component = menu.component.replace(/^\/+/, '');
              routes.push(
                <Route 
                  key={menu.id || menu.name || menu.path} 
                  path={relativePath} 
                  element={getDynamicComponent(component)} 
                />
              );
              
              if (hasChildren) {
                const childRoutes = renderNestedRoutesWithPathCollection(menu.children, parentFullPath, isNested);
                routes.push(...childRoutes);
              }
            }
          } else if (hasChildren) {
            const childRoutes = renderNestedRoutesWithPathCollection(menu.children, parentFullPath, isNested);
            routes.push(...childRoutes);
          } else if (hasComponent && !hasPath) {
            console.log('跳过无路径但有组件的菜单:', menu.name);
          }
        });
        
        return routes;
      };
      
      const generatedRoutes = renderNestedRoutesWithPathCollection(profileMenus);

      const firstSystemPath = getFirstMigratedSystemManagePathFromMenus(profileMenus);

      // 优先使用实际生成的路由路径（第一个），确保跳转的路径对应的路由确实存
      const firstValidPath = collectedPaths.length > 0 ? collectedPaths[0] : findFirstValidPath(profileMenus);

      // 只有当找到了有效路径且生成了路由时，才进行跳
      const canNavigate = firstValidPath && generatedRoutes.length > 0;

      return (
        <Route 
          key={path} 
          path={path} 
          element={<Auth><ProtectedComponent /></Auth>}
        >
          <Route
            path="seriesManage"
            element={<Navigate to={PREMIUM_SERIES_MANAGE_FULL_PATH} replace />}
          />
          <Route path="basic" element={<Navigate to={`${PREMIUM_SYSTEM_MANAGE_BASE}/basic`} replace />} />
          <Route path="user" element={<Navigate to={`${PREMIUM_SYSTEM_MANAGE_BASE}/user`} replace />} />
          <Route path="role" element={<Navigate to={`${PREMIUM_SYSTEM_MANAGE_BASE}/role`} replace />} />
          <Route path="menu" element={<Navigate to={`${PREMIUM_SYSTEM_MANAGE_BASE}/menu`} replace />} />
          <Route path="voice" element={<Navigate to={`${PREMIUM_SYSTEM_MANAGE_BASE}/voice`} replace />} />
          <Route path="voiceManage" element={<Navigate to={`${PREMIUM_SYSTEM_MANAGE_BASE}/voice`} replace />} />
          <Route path="system/voice" element={<Navigate to={`${PREMIUM_SYSTEM_MANAGE_BASE}/voice`} replace />} />
          <Route path="system/voiceManage" element={<Navigate to={`${PREMIUM_SYSTEM_MANAGE_BASE}/voice`} replace />} />
          <Route path="log/loginlog" element={<Navigate to={`${PREMIUM_SYSTEM_MANAGE_BASE}/log/loginlog`} replace />} />
          <Route path="log/operationlog" element={<Navigate to={`${PREMIUM_SYSTEM_MANAGE_BASE}/log/operationlog`} replace />} />
          <Route path="system/log/loginlog" element={<Navigate to={`${PREMIUM_SYSTEM_MANAGE_BASE}/log/loginlog`} replace />} />
          <Route path="system/log/operationlog" element={<Navigate to={`${PREMIUM_SYSTEM_MANAGE_BASE}/log/operationlog`} replace />} />
          {/* 递归生成所有层级的嵌套路由 */}
          {generatedRoutes}
          {/* Profile 页面的默认子路由 */}
          <Route 
            index 
            element={
              canNavigate ? (
                <Navigate to={firstValidPath} replace />
              ) : firstSystemPath ? (
                <Navigate to={firstSystemPath} replace />
              ) : (
                <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                  <h3>暂无可用功能</h3>
                  <p>当前账号在该模块下没有可访问的菜单，请联系管理员配置</p>
                </div>
              )
            } 
          />
          {/* 未匹配子路由：有留在 profile 的子路由则回默认 profile 子页；否则进工作system-manage 或精品译*/}
          {(generatedRoutes.length > 0 || firstSystemPath) && (
            <Route
              path="*"
              element={
                <Navigate
                  to={
                    generatedRoutes.length > 0 && firstValidPath
                      ? firstValidPath
                      : firstSystemPath || PREMIUM_TRANSLATION_FULL_PATH
                  }
                  replace
                />
              }
            />
          )}
        </Route>
      );
    }
    
    if (requireAuth) {
      return (
        <Route 
          key={path} 
          path={path} 
          element={<Auth><ProtectedComponent /></Auth>} 
        />
      );
    } else {
      return (
        <Route 
          key={path} 
          path={path} 
          element={<ProtectedComponent />} 
        />
      );
    }
  };

  /** 不盖全屏加载层：`/hub` 等业务路由已静态注册，菜单接口在后台合并即可；首屏侧栏可用 globalStore 已有菜单 */
  return (
    <Routes>
        <Route
          path="/quick-translate"
          element={
            <Auth>
              <PermissionGuard permission={undefined}>
                <QuickTrans />
              </PermissionGuard>
            </Auth>
          }
        />
        {/* 旧 /marketing 落地页路由已迁移至 /home */}
        <Route path="/marketing" element={<Navigate to={MARKETING_PAGE_PATH} replace />} />
        {/** 静/hub 须在菜单平铺 Route 之前，避免与菜单推导的平path 竞*/}
        <Route path={APP_LAYOUT_BASE} element={<HomeRootLayout />}>
          <Route index element={<Navigate to="premium-translation" replace />} />
          <Route
            element={
              <Auth>
                <PermissionGuard permission={undefined}>
                  <PremiumWorkspaceLayout />
                </PermissionGuard>
              </Auth>
            }
          >
            <Route path="premium-translation" element={<Home />} />
            <Route path="quick-translate" element={<QuickTranslateHome />} />
            <Route path="premium-series" element={<PremiumSeriesInnerPage />} />
            <Route path="quick-series-inner" element={<QuickSeriesListIn />} />
            <Route
              path="premium-video-list-refactor"
              element={
                <React.Suspense fallback={<PageLoading text="加载视频列表" />}>
                  <PremiumVideoListRefactorPage />
                </React.Suspense>
              }
            />
            <Route path="premium-upload-video" element={<PremiumUploadVideoWorkspacePage />} />
            <Route
              path="voice-library/design"
              element={<Navigate to={PREMIUM_VOICE_DESIGN_FULL_PATH} replace />}
            />
            {/* 【页面下线保留】音色库主页面（/hub/voice-library）已下线
                代码保留以便后续大清理时通过读取此注释彻底删除相关文件
                下次大清理需删除的文件：src/pages/voiceLibrary/ 目录下的 Index.tsx
                VoiceLibraryWorkspacePage.tsx、VoiceLibraryWorkspacePage.scss
                VoiceLibrary.scss、VoiceLibraryEntryRedirect.tsx 等相关文*/}
            <Route path="voice-library" element={<VoiceLibraryWorkspaceOutlet />}>
              <Route index element={<Navigate to="public" replace />} />
              <Route path="public" element={<VoicePublic embedded />} />
              <Route path="series" element={<Navigate to=".." replace />} />
            </Route>
            <Route path="voice-design" element={<VoiceDesignWorkspacePage />} />
            <Route path="voice-clone" element={<VoiceClone />} />
            <Route path="ai-voice" element={<VoiceAi />} />
            <Route path="ai-image" element={<AiImage />} />
            <Route path="ai-image/detail" element={<AiImageDetail />} />
            <Route path="text-to-audio" element={<TextToAudio />} />
            <Route path="podcast" element={<AiPodcast />} />
            <Route path="ai-video" element={<AiVideo />} />
            <Route path="ai-video/detail" element={<AiVideoDetail />} />
            <Route path="ai-digital-human" element={<AiDigitalHuman />}>
              <Route index element={<AiDigitalHumanStudio />} />
              <Route path="avatars" element={<AiDigitalHumanAvatars />} />
              <Route path="templates" element={<AiDigitalHumanTemplates />} />
            </Route>
            <Route path="transcription" element={<AiTranscription />} />
            <Route path="invoice-management" element={<InvoiceManagement />} />
            <Route path="subtitle-trans" element={<SubtitleTrans />} />
            <Route path="seriesManage" element={getDynamicComponent("pages/manage/JujiManage")} />
            <Route path="system-manage" element={<SystemManageWorkspaceLayout />}>
              <Route index element={<Navigate to="basic" replace />} />
              <Route
                path="basic"
                element={
                  <PermissionGuard routePath="/hub/system-manage/basic">
                    {getDynamicComponent("pages/manage/BasicInfo")}
                  </PermissionGuard>
                }
              />
              <Route
                path="user"
                element={
                  <PermissionGuard routePath="/hub/system-manage/user">
                    {getDynamicComponent("pages/manage/system/UserInfo")}
                  </PermissionGuard>
                }
              />
              <Route
                path="role"
                element={
                  <PermissionGuard routePath="/hub/system-manage/role">
                    {getDynamicComponent("pages/manage/system/RoleManage")}
                  </PermissionGuard>
                }
              />
              <Route
                path="voice"
                element={
                  <PermissionGuard routePath="/hub/system-manage/voice">
                    {getDynamicComponent("pages/manage/system/voiceManage")}
                  </PermissionGuard>
                }
              />
              <Route
                path="menu"
                element={
                  <PermissionGuard routePath="/hub/system-manage/menu">
                    {getDynamicComponent("pages/manage/system/MenuManage")}
                  </PermissionGuard>
                }
              />
              <Route
                path="log/loginlog"
                element={
                  <PermissionGuard routePath="/hub/system-manage/log/loginlog">
                    {getDynamicComponent("pages/manage/system/log/Loginlog")}
                  </PermissionGuard>
                }
              />
              <Route
                path="log/operationlog"
                element={
                  <PermissionGuard routePath="/hub/system-manage/log/operationlog">
                    {getDynamicComponent("pages/manage/system/log/Operationlog")}
                  </PermissionGuard>
                }
              />
            </Route>
          </Route>
        </Route>
        {routes
          .filter((r) => !PREMIUM_ROUTE_PATHS.has(r.path))
          .map((route) => renderRoute(route, menuList))}
        <Route path="/" element={<RootRedirect />} />
      </Routes>
  );
};
